/**
 * stepper-flow -- Meta-modo wizard N pasos sobre ActionArea v1.0.
 *
 * Renderiza un wizard con barra de progreso + cuerpo dinamico (delegado a sub-modos)
 * + footer con botones Anterior / Siguiente / Finalizar. Persiste estado en
 * localStorage clave "nx_stepper_<slug-de-title>" para retomar el flujo donde quedo
 * el usuario tras refresh / cierre.
 *
 * Sub-modos soportados (rendereados internamente):
 *   - text-image      → ./text-image.js
 *   - video           → ./video.js
 *   - quiz-embedded   → ./quiz-embedded.js
 *   - inline-upload   → reusa renderInlineUpload de ActionArea (delegado via ctx.renderSubMode)
 *   - table-results   → reusa renderTableResults de ActionArea (delegado)
 *
 * Payload (lo que recibe showMode('stepper-flow', payload)):
 *   - title       (string, requerido) - titulo del wizard (slug -> localStorage key)
 *   - steps       (array{type, ...stepData}, requerido) - definicion de pasos
 *   - onComplete  (function, opcional) - invoked con { results: stepResults[] } al terminar
 *   - onExit      (function, opcional) - invoked si el usuario cierra antes de completar
 *   - resumePolicy ('auto'|'prompt'|'fresh', default 'prompt')
 *
 * Persistencia (clave: nx_stepper_<slug>):
 *   { currentStep: number, results: array<any|null>, ts: ISO8601, totalSteps: number }
 *
 * Eventos emitidos a la ActionArea (ctx.emit):
 *   - 'mode-action': { mode:'stepper-flow', action:'step-change'|'completed'|'exited', data }
 *
 * @module presentation/components/ActionAreaModes/stepper-flow
 */

import { renderTextImage } from './text-image.js';
import { renderVideo } from './video.js';
import { renderQuizEmbedded } from './quiz-embedded.js';

const LS_PREFIX = 'nx_stepper_';

const slugify = (s) => String(s || 'wizard')
  .toLowerCase()
  .replace(/[^\w\s-]/g, '')
  .trim()
  .replace(/[\s_-]+/g, '-')
  .slice(0, 60) || 'wizard';

/**
 * Handler registrado en MODES de ActionArea. Firma estandar (container, payload, ctx).
 */
export function renderStepperFlow(container, payload, ctx) {
  const title = payload?.title || 'Wizard';
  const steps = Array.isArray(payload?.steps) ? payload.steps : [];
  const resumePolicy = payload?.resumePolicy || 'prompt';
  const onComplete = typeof payload?.onComplete === 'function' ? payload.onComplete : null;
  const onExit = typeof payload?.onExit === 'function' ? payload.onExit : null;

  if (steps.length === 0) {
    container.innerHTML = `
      <div style="padding:18px;color:#f85149;font-size:0.9rem">
        stepper-flow: <code>steps</code> vacio o no es array
      </div>
    `;
    return;
  }

  expandContainerStepper(container);

  const lsKey = LS_PREFIX + slugify(title);
  const total = steps.length;

  // Estado interno del flow
  const flowState = {
    currentStep: 0,
    results: new Array(total).fill(null),
    startedAt: new Date().toISOString()
  };

  // Intentar restaurar estado previo
  const restored = loadFromStorage(lsKey, total);
  if (restored) {
    if (resumePolicy === 'auto') {
      Object.assign(flowState, restored);
    } else if (resumePolicy === 'fresh') {
      clearFromStorage(lsKey);
    } else {
      // 'prompt': mostrar pregunta antes de arrancar
      promptResume(container, ctx, restored, () => {
        Object.assign(flowState, restored);
        renderFrame();
      }, () => {
        clearFromStorage(lsKey);
        renderFrame();
      });
      return; // espera respuesta del usuario
    }
  }

  renderFrame();

  // ============== Render principal ==============

  function renderFrame() {
    const idx = flowState.currentStep;
    const step = steps[idx];
    const isFirst = idx === 0;
    const isLast = idx === total - 1;
    const stepProgressPct = Math.round(((idx + 1) / total) * 100);

    container.innerHTML = `
      <div class="aa-stepper" data-aa-substep-type="stepper-flow">
        <header style="display:flex;align-items:center;justify-content:space-between;gap:10px;padding:14px 16px 8px">
          <div style="min-width:0;flex:1">
            <h3 style="margin:0;font-size:0.98rem;color:#f0f6fc;line-height:1.3">${ctx.escHtml(title)}</h3>
            <div style="margin-top:4px;font-size:0.875rem;color:#8b949e">Paso ${idx + 1} de ${total}${step?.label ? ' · ' + ctx.escHtml(step.label) : ''}</div>
          </div>
          <button data-aa-stepper-close type="button" title="Cerrar wizard"
                  style="background:transparent;border:none;color:#8b949e;cursor:pointer;font-size:1.1rem;padding:0 4px">×</button>
        </header>
        <div class="aa-stepper-progress" style="height:4px;background:#21262d;border-radius:2px;margin:0 16px;overflow:hidden">
          <div data-aa-stepper-bar style="width:${stepProgressPct}%;height:100%;background:linear-gradient(90deg,#1f6feb,#58a6ff);transition:width 0.25s"></div>
        </div>
        <div data-aa-stepper-body class="aa-stepper-body" style="padding:14px 16px 4px;max-height:340px;overflow-y:auto"></div>
        <footer style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 16px 14px;border-top:1px solid #21262d;margin-top:8px">
          <button data-aa-stepper-back type="button" ${isFirst ? 'disabled' : ''}
                  style="padding:6px 14px;background:transparent;border:1px solid #30363d;border-radius:5px;color:#c9d1d9;cursor:${isFirst ? 'default' : 'pointer'};font-size:0.875rem;${isFirst ? 'opacity:0.4' : ''}">
            ← Anterior
          </button>
          <div style="font-size:0.875rem;color:#6e7681">${stepProgressPct}%</div>
          <button data-aa-stepper-next type="button"
                  style="padding:6px 16px;background:#1f6feb;border:1px solid #1f6feb;border-radius:5px;color:#fff;cursor:pointer;font-size:0.875rem">
            ${isLast ? 'Finalizar ✓' : 'Siguiente →'}
          </button>
        </footer>
      </div>
    `;

    const $body = container.querySelector('[data-aa-stepper-body]');
    const $back = container.querySelector('[data-aa-stepper-back]');
    const $next = container.querySelector('[data-aa-stepper-next]');
    const $close = container.querySelector('[data-aa-stepper-close]');

    // Renderizar el cuerpo del paso actual via sub-mode renderer
    renderStepBody($body, step, idx);

    // Wire footer buttons
    $back.addEventListener('click', () => {
      if (flowState.currentStep > 0) {
        flowState.currentStep--;
        persistState();
        renderFrame();
      }
    });
    $next.addEventListener('click', () => {
      if (flowState.currentStep < total - 1) {
        flowState.currentStep++;
        persistState();
        ctx.emit('mode-action', { mode: 'stepper-flow', action: 'step-change', data: { from: idx, to: flowState.currentStep, total } });
        renderFrame();
      } else {
        complete();
      }
    });
    $close.addEventListener('click', () => exit());
  }

  /**
   * Delega el render del paso al sub-mode segun step.type.
   */
  function renderStepBody($body, step, idx) {
    const stepCtx = {
      escHtml: ctx.escHtml,
      recordResult: (data) => {
        flowState.results[idx] = data;
        persistState();
      },
      goNext: () => {
        if (flowState.currentStep < total - 1) {
          flowState.currentStep++;
          persistState();
          renderFrame();
        } else {
          complete();
        }
      },
      goBack: () => {
        if (flowState.currentStep > 0) {
          flowState.currentStep--;
          persistState();
          renderFrame();
        }
      }
    };

    const type = step?.type || 'text-image';
    switch (type) {
      case 'text-image':
        renderTextImage($body, step, stepCtx);
        break;
      case 'video':
        renderVideo($body, step, stepCtx);
        break;
      case 'quiz-embedded':
        renderQuizEmbedded($body, step, stepCtx);
        break;
      case 'inline-upload':
        renderNestedInlineUpload($body, step, stepCtx);
        break;
      case 'table-results':
        renderNestedTableResults($body, step, stepCtx);
        break;
      default:
        $body.innerHTML = `
          <div style="padding:14px;color:#d29922;font-size:0.875rem;background:#161b22;border:1px dashed #d29922;border-radius:6px">
            Sub-modo desconocido: <code>${ctx.escHtml(type)}</code>. Tipos validos:
            text-image, video, quiz-embedded, inline-upload, table-results.
          </div>
        `;
        stepCtx.recordResult({ skipped: true, reason: 'unknown-type' });
    }
  }

  /**
   * Sub-modo nested inline-upload: variante simplificada (no pide cierre del container,
   * solo registra archivo y marca step como completado).
   */
  function renderNestedInlineUpload($body, step, stepCtx) {
    const label = step?.label || 'Subir archivo';
    const accept = step?.accept || 'image/png,image/jpeg,image/webp,application/pdf';
    const maxSize = step?.maxSize || 5 * 1024 * 1024;

    $body.innerHTML = `
      <div data-aa-substep-type="inline-upload" style="display:flex;flex-direction:column;gap:10px">
        <div style="font-size:0.88rem;color:#c9d1d9">${stepCtx.escHtml(label)}</div>
        <input type="file" data-aa-nested-upload accept="${stepCtx.escHtml(accept)}"
               style="padding:8px;background:#0d1117;border:1px dashed #30363d;border-radius:6px;color:#c9d1d9;font-size:0.875rem;cursor:pointer"/>
        <div data-aa-nested-upload-info style="font-size:0.875rem;color:#8b949e">Sin archivo seleccionado</div>
      </div>
    `;
    const $input = $body.querySelector('[data-aa-nested-upload]');
    const $info = $body.querySelector('[data-aa-nested-upload-info]');
    $input.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (!file) {
        $info.textContent = 'Sin archivo seleccionado';
        $info.style.color = '#8b949e';
        return;
      }
      if (file.size > maxSize) {
        $info.textContent = `Archivo excede ${(maxSize / 1024 / 1024).toFixed(1)}MB`;
        $info.style.color = '#f85149';
        stepCtx.recordResult({ uploaded: false, error: 'too-large' });
        return;
      }
      $info.textContent = `${file.name} · ${(file.size / 1024).toFixed(1)} KB`;
      $info.style.color = '#3fb950';
      stepCtx.recordResult({ uploaded: true, fileName: file.name, sizeBytes: file.size, mimeType: file.type });
    });
  }

  /**
   * Sub-modo nested table-results: tabla read-only para mostrar datos del paso.
   */
  function renderNestedTableResults($body, step, stepCtx) {
    const rows = Array.isArray(step?.rows) ? step.rows : [];
    const headers = Array.isArray(step?.headers) ? step.headers : (rows[0] ? Object.keys(rows[0]) : []);
    const tableTitle = step?.tableTitle || 'Datos';

    const headerHtml = headers.map(h =>
      `<th style="padding:6px 10px;text-align:left;border-bottom:1px solid #30363d;font-size:0.875rem;color:#8b949e;text-transform:uppercase">${stepCtx.escHtml(h)}</th>`
    ).join('');
    const rowsHtml = rows.map(r => {
      const cells = headers.map(h => `<td style="padding:6px 10px;border-bottom:1px solid #21262d;font-size:0.875rem;color:#c9d1d9">${stepCtx.escHtml(r[h] ?? '-')}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');

    $body.innerHTML = `
      <div data-aa-substep-type="table-results" style="display:flex;flex-direction:column;gap:8px">
        <div style="font-size:0.88rem;color:#c9d1d9">${stepCtx.escHtml(tableTitle)} <span style="color:#8b949e">(${rows.length})</span></div>
        <div style="border:1px solid #30363d;border-radius:6px;background:#0d1117;overflow:auto;max-height:240px">
          <table style="border-collapse:collapse;width:100%">
            <thead style="position:sticky;top:0;background:#161b22"><tr>${headerHtml}</tr></thead>
            <tbody>${rowsHtml || `<tr><td colspan="${headers.length || 1}" style="padding:14px;text-align:center;color:#8b949e">Sin filas</td></tr>`}</tbody>
          </table>
        </div>
      </div>
    `;
    stepCtx.recordResult({ shown: true, rowCount: rows.length });
  }

  function complete() {
    persistState({ completedAt: new Date().toISOString() });
    ctx.emit('mode-action', { mode: 'stepper-flow', action: 'completed', data: { results: flowState.results.slice(), totalSteps: total } });
    if (onComplete) {
      try { onComplete({ results: flowState.results.slice() }); }
      catch (e) { console.error('[stepper-flow] onComplete threw:', e); }
    }
    clearFromStorage(lsKey);
    ctx.clear();
  }

  function exit() {
    persistState();
    ctx.emit('mode-action', { mode: 'stepper-flow', action: 'exited', data: { atStep: flowState.currentStep, results: flowState.results.slice() } });
    if (onExit) {
      try { onExit({ atStep: flowState.currentStep, results: flowState.results.slice() }); }
      catch (e) { console.error('[stepper-flow] onExit threw:', e); }
    }
    ctx.clear();
  }

  function persistState(extra = {}) {
    try {
      const snapshot = {
        currentStep: flowState.currentStep,
        results: flowState.results,
        totalSteps: total,
        ts: new Date().toISOString(),
        ...extra
      };
      localStorage.setItem(lsKey, JSON.stringify(snapshot));
    } catch (e) {
      console.warn('[stepper-flow] persistState fallo:', e?.message || e);
    }
  }
}

// ============== Helpers de persistencia ==============

function loadFromStorage(key, expectedTotal) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data.currentStep !== 'number') return null;
    if (data.totalSteps !== expectedTotal) {
      // Steps cambiaron desde la ultima vez - estado invalido
      localStorage.removeItem(key);
      return null;
    }
    if (data.currentStep <= 0 || data.currentStep >= expectedTotal) return null;
    return {
      currentStep: data.currentStep,
      results: Array.isArray(data.results) && data.results.length === expectedTotal
        ? data.results
        : new Array(expectedTotal).fill(null)
    };
  } catch {
    return null;
  }
}

function clearFromStorage(key) {
  try { localStorage.removeItem(key); } catch {}
}

function promptResume(container, ctx, restored, onResume, onFresh) {
  expandContainerStepper(container);
  container.innerHTML = `
    <div style="padding:22px;text-align:center;display:flex;flex-direction:column;gap:14px">
      <h3 style="margin:0;color:#f0f6fc;font-size:1rem">¿Retomar el flujo?</h3>
      <p style="margin:0;color:#c9d1d9;font-size:0.875rem;line-height:1.45">
        Hay progreso guardado: paso <strong>${restored.currentStep + 1}</strong>.<br/>
        Podes continuar donde quedaste o empezar de cero.
      </p>
      <div style="display:flex;gap:10px;justify-content:center">
        <button data-aa-stepper-fresh type="button"
                style="padding:8px 16px;background:transparent;border:1px solid #30363d;border-radius:5px;color:#c9d1d9;cursor:pointer;font-size:0.875rem">↺ Empezar de cero</button>
        <button data-aa-stepper-resume type="button"
                style="padding:8px 16px;background:#1f6feb;border:1px solid #1f6feb;border-radius:5px;color:#fff;cursor:pointer;font-size:0.875rem">→ Continuar</button>
      </div>
    </div>
  `;
  container.querySelector('[data-aa-stepper-resume]').addEventListener('click', onResume);
  container.querySelector('[data-aa-stepper-fresh]').addEventListener('click', onFresh);
}

function expandContainerStepper(container) {
  Object.assign(container.style, {
    maxHeight: '560px',
    opacity: '1',
    margin: '12px 0',
    border: '1px solid #1f6feb',
    padding: '0'
  });
}
